import { GUIDE_NOTICE } from './copy';
import { postGuideChatLine } from './messages';

/** How long to wait for `[data-guide]` before telling the user it is missing. */
export const GUIDE_ANCHOR_TIMEOUT_MS = 5_000;

const FOCUSABLE = 'input:not([disabled]), textarea:not([disabled]), button:not([disabled])';

export type GuideAnchorStatus = 'idle' | 'pending' | 'found' | 'missing';

export interface AnchorWatchHost {
  isCurrent: (stepId: string) => boolean;
  getAnchor: () => HTMLElement | null;
  getStatus: () => GuideAnchorStatus;
  setAnchor: (element: HTMLElement | null, status: GuideAnchorStatus) => void;
}

let watchToken = 0;
let observer: MutationObserver | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

function anchorSelector(anchorId: string): string {
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(anchorId)
      : anchorId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `[data-guide="${escaped}"]`;
}

function findAnchor(anchorId: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const element = document.querySelector(anchorSelector(anchorId));
  return element instanceof HTMLElement ? element : null;
}

/** Focus the anchor when it is a field, otherwise its first input, textarea, or button. */
export function focusGuideAnchor(root: HTMLElement): void {
  const target = root.matches(FOCUSABLE)
    ? root
    : root.querySelector<HTMLElement>(FOCUSABLE);
  if (!target) return;
  try {
    target.focus();
  } catch {
    // A field inside a closed container can refuse focus. The highlight still shows.
  }
}

function disconnectWatch(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  observer?.disconnect();
  observer = null;
}

/** Drop any in-flight anchor wait. Safe to call when nothing is watching. */
export function stopGuideAnchorWatch(): void {
  watchToken += 1;
  disconnectWatch();
}

/**
 * Resolve `[data-guide="<anchor>"]`, waiting on DOM mutations. Never throws.
 * A miss posts the anchor-not-found notice once and leaves the guide active
 * so the user can skip or stop. The observer stays up so a late mount still highlights.
 */
export function beginGuideAnchorWatch(
  anchorId: string,
  stepId: string,
  focus: boolean,
  host: AnchorWatchHost,
): void {
  stopGuideAnchorWatch();
  const token = watchToken;
  let focused = false;
  let reportedMissing = false;

  const still = (): boolean => token === watchToken && host.isCurrent(stepId);

  const applyFound = (element: HTMLElement): void => {
    if (!still()) return;
    if (host.getAnchor() !== element || host.getStatus() !== 'found') {
      host.setAnchor(element, 'found');
    }
    if (focus && !focused) {
      focused = true;
      focusGuideAnchor(element);
    }
  };

  const existing = findAnchor(anchorId);
  if (existing) {
    applyFound(existing);
  } else if (still()) {
    host.setAnchor(null, 'pending');
  }

  if (typeof document !== 'undefined' && document.body && typeof MutationObserver === 'function') {
    try {
      observer = new MutationObserver(() => {
        const element = findAnchor(anchorId);
        if (element) applyFound(element);
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-guide'],
      });
    } catch {
      observer = null;
    }
  }

  timer = setTimeout(() => {
    timer = null;
    if (!still() || reportedMissing) return;
    const element = findAnchor(anchorId);
    if (element) {
      applyFound(element);
      return;
    }
    reportedMissing = true;
    host.setAnchor(null, 'missing');
    postGuideChatLine({ guideNoticeKey: GUIDE_NOTICE.anchorNotFound });
  }, GUIDE_ANCHOR_TIMEOUT_MS);
}
